import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import SEOMeta from "@/components/SEOMeta";
import SchemaMarkup from "@/components/SchemaMarkup";
import FAQ from "@/components/FAQ";
import { 
  Camera, 
  Building2, 
  Construction, 
  ShieldCheck, 
  Clock, 
  CheckCircle2,
  Antenna,
  FileText,
  TrendingUp,
  MapPin,
  Phone,
  Mail
} from "lucide-react";
import heroImage from "@/assets/drone-inspection-hero.jpg";
import apartmentInspectionImage from "@/assets/drone-apartment-inspection.jpg";
import constructionSiteImage from "@/assets/drone-construction-site-inspection.jpg";
import commercialPlazaImage from "@/assets/drone-commercial-plaza-inspection.jpg";

const DroneInspectionServices = () => {
  const faqData = [
    {
      question: "What types of properties can be inspected with drones?",
      answer: "Our drone inspection services cover all property types including residential homes, apartment buildings, commercial plazas, industrial facilities, construction sites, and infrastructure projects. We can inspect roofs, facades, chimneys, HVAC systems, and hard-to-reach areas safely and efficiently."
    },
    {
      question: "How much does a drone building inspection cost in the GTA?",
      answer: "Drone inspection costs vary based on property size, complexity, and inspection scope. Residential inspections typically range from $300-$800, while commercial building inspections start at $1,000. Contact us for a free quote tailored to your specific property in Toronto, Hamilton, or Niagara Region."
    },
    {
      question: "Are drone inspections as accurate as traditional inspections?",
      answer: "Yes, drone inspections often provide more accurate results than traditional methods. Our 4K cameras and thermal imaging equipment capture high-resolution images and videos that reveal issues invisible to the naked eye. Drones also access dangerous or hard-to-reach areas safely without scaffolding or ladders."
    },
    {
      question: "How long does a drone inspection take?",
      answer: "Most residential drone inspections take 30-60 minutes, while commercial properties may require 1-3 hours depending on size. We typically deliver detailed inspection reports with photos and videos within 24-48 hours of the inspection date."
    },
    {
      question: "Do you need permission to fly drones in Toronto and Hamilton?",
      answer: "Yes, we maintain all required Transport Canada certifications and follow strict safety protocols. We handle all necessary permits and permissions for drone flights in Toronto, Hamilton, Niagara, and throughout the GTA. Our pilots are fully licensed and insured for commercial operations."
    },
    {
      question: "What equipment do you use for drone inspections?",
      answer: "We use professional-grade inspection drones equipped with 4K cameras, thermal imaging sensors, and advanced stabilization systems. Our fleet includes multiple aircraft suitable for different inspection scenarios, ensuring we capture the highest quality data for every project."
    }
  ];

  const inspectionTypes = [
    {
      icon: Building2,
      title: "Apartment & Condo Inspections",
      description: "Comprehensive aerial inspections of multi-unit residential buildings including roof condition assessments, facade inspections, and structural evaluations.",
      image: apartmentInspectionImage,
      features: ["Roof & Membrane Assessment", "Building Envelope Inspection", "Balcony & Railing Review", "Parking Structure Analysis"]
    },
    {
      icon: Construction,
      title: "Construction Site Monitoring",
      description: "Track construction progress, verify compliance, document site conditions, and enhance safety with regular aerial surveys and progress reports.",
      image: constructionSiteImage,
      features: ["Progress Documentation", "Safety Compliance Monitoring", "Volumetric Measurements", "Site Mapping & Surveying"]
    },
    {
      icon: ShieldCheck,
      title: "Commercial Property Inspections",
      description: "Detailed inspections of shopping centers, office buildings, warehouses, and industrial facilities with thermal imaging and high-resolution photography.",
      image: commercialPlazaImage,
      features: ["Roof Condition Reports", "HVAC System Inspection", "Structural Integrity Review", "Thermal Energy Audits"]
    }
  ];

  const benefits = [
    {
      icon: Clock,
      title: "Fast & Efficient",
      description: "Complete inspections in a fraction of the time required for traditional methods, with minimal disruption to operations."
    },
    {
      icon: ShieldCheck,
      title: "Enhanced Safety",
      description: "Eliminate the need for dangerous ladder work, scaffolding, or roof access. Keep your team safe while getting superior results."
    },
    {
      icon: Camera,
      title: "High-Resolution Documentation",
      description: "4K video and photography with thermal imaging capabilities provide unmatched detail and accuracy for every inspection."
    },
    {
      icon: TrendingUp,
      title: "Cost-Effective Solutions",
      description: "Reduce inspection costs by up to 50% compared to traditional methods while improving data quality and turnaround time."
    },
    {
      icon: FileText,
      title: "Detailed Reporting",
      description: "Receive comprehensive inspection reports with annotated photos, videos, and thermal imaging data within 24-48 hours."
    },
    {
      icon: Antenna,
      title: "Advanced Technology",
      description: "State-of-the-art drone equipment with GPS positioning, obstacle avoidance, and professional-grade imaging sensors."
    }
  ];

  const serviceAreas = [
    { name: "Toronto", description: "Downtown, North York, Scarborough, Etobicoke" },
    { name: "Hamilton", description: "Downtown, Stoney Creek, Dundas, Ancaster" },
    { name: "Niagara Region", description: "St. Catharines, Niagara Falls, Welland, Port Colborne" },
    { name: "GTA", description: "Mississauga, Brampton, Markham, Vaughan, Oakville" }
  ];

  return (
    <>
      <SEOMeta 
        title="Drone Building Inspections Toronto, Hamilton, Niagara | Aerial Property Inspection Services GTA"
        description="Professional drone building inspection services in Toronto, Hamilton & Niagara Region. Fast, safe aerial inspections for commercial buildings, apartments, construction sites. Licensed & insured. Free quotes."
        keywords="drone building inspection Toronto, aerial inspection Hamilton, drone roof inspection Niagara, commercial property inspection GTA, construction site drone survey, apartment building inspection, thermal imaging inspection Ontario"
        canonicalUrl="https://blueknightcontracting.ca/services/drone-inspections"
        ogImage={heroImage}
      />
      
      <SchemaMarkup
        type="Service"
        data={{
          serviceType: "Drone Building Inspection Services",
          serviceName: "Professional Drone & Aerial Inspection Solutions",
          services: [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Apartment Building Drone Inspections Toronto",
                "description": "Comprehensive aerial inspections of multi-unit residential buildings with 4K imaging and thermal analysis."
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Construction Site Monitoring Hamilton",
                "description": "Progress tracking, safety compliance monitoring, and site documentation with professional drone technology."
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Commercial Property Inspections Niagara",
                "description": "Detailed aerial surveys of commercial buildings, roofing systems, and infrastructure with thermal imaging."
              }
            }
          ]
        }}
      />

      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${heroImage})`,
            }}
          />
          <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Professional Drone Building Inspections
            </h1>
            <p className="text-xl md:text-2xl mb-8">
              Advanced Aerial Inspection Services in Toronto, Hamilton & Niagara Region
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="default" asChild>
                <Link to="/contact">Get Free Quote</Link>
              </Button>
              <Button size="lg" variant="outline" asChild className="bg-white/10 text-white border-white hover:bg-white/20">
                <a href="tel:4165552583">Call (416) 555-2583</a>
              </Button>
            </div>
          </div>
        </section>

        {/* Introduction */}
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold mb-6">
                  Safe, Fast & Accurate Building Inspections with Drone Technology
                </h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Blue Knight Contracting provides cutting-edge drone inspection services for residential, commercial, and industrial properties throughout the Greater Toronto Area, Hamilton, and Niagara Region. Our Transport Canada-certified pilots use professional-grade drones equipped with 4K cameras and thermal imaging to deliver detailed, accurate inspection reports.
                </p>
                <p className="text-lg text-muted-foreground mb-6">
                  Whether you need a roof inspection, facade assessment, construction progress monitoring, or comprehensive building survey, our aerial inspection services provide superior results at a fraction of the cost and time of traditional methods.
                </p>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-primary" />
                    <span className="font-semibold">Transport Canada Certified</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-primary" />
                    <span className="font-semibold">Fully Licensed & Insured</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-primary" />
                    <span className="font-semibold">24-48 Hour Reports</span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <Camera className="h-8 w-8 text-primary mb-2" />
                    <CardTitle className="text-xl">4K Imaging</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      Ultra high-resolution photography and video documentation
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <ShieldCheck className="h-8 w-8 text-primary mb-2" />
                    <CardTitle className="text-xl">Safe Access</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      No scaffolding or dangerous ladder work required
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <Clock className="h-8 w-8 text-primary mb-2" />
                    <CardTitle className="text-xl">Fast Results</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      Complete inspections in hours, not days
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <FileText className="h-8 w-8 text-primary mb-2" />
                    <CardTitle className="text-xl">Detailed Reports</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      Comprehensive documentation with analysis
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Inspection Types */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Our Drone Inspection Services</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Comprehensive aerial inspection solutions for every property type and inspection need
              </p>
            </div>

            <div className="space-y-12">
              {inspectionTypes.map((type, index) => (
                <Card key={index} className="overflow-hidden">
                  <div className={`grid md:grid-cols-2 gap-6 ${index % 2 === 1 ? 'md:grid-flow-dense' : ''}`}>
                    <div className={`relative h-[300px] ${index % 2 === 1 ? 'md:col-start-2' : ''}`}>
                      <img 
                        src={type.image} 
                        alt={`${type.title} - Professional drone inspection services in Ontario`}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-6 flex flex-col justify-center">
                      <type.icon className="h-12 w-12 text-primary mb-4" />
                      <CardTitle className="text-2xl mb-3">{type.title}</CardTitle>
                      <CardDescription className="text-base mb-4">
                        {type.description}
                      </CardDescription>
                      <ul className="space-y-2">
                        {type.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center gap-2">
                            <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Why Choose Drone Inspections?</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Discover the advantages of modern aerial inspection technology for your property
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => (
                <Card key={index}>
                  <CardHeader>
                    <benefit.icon className="h-10 w-10 text-primary mb-3" />
                    <CardTitle className="text-xl">{benefit.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{benefit.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Inspection Process */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Our Drone Inspection Process</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Simple, efficient, and transparent from start to finish
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-8">
              {[
                {
                  step: "01",
                  title: "Initial Consultation",
                  description: "Discuss your inspection needs, property details, and specific concerns. We'll provide a detailed quote and timeline."
                },
                {
                  step: "02",
                  title: "Pre-Flight Planning",
                  description: "Our team reviews property location, obtains necessary permits, and plans the optimal flight path for comprehensive coverage."
                },
                {
                  step: "03",
                  title: "On-Site Inspection",
                  description: "Licensed pilots conduct the aerial inspection using professional drones with 4K cameras and thermal imaging equipment."
                },
                {
                  step: "04",
                  title: "Report Delivery",
                  description: "Receive a detailed inspection report with annotated photos, videos, and professional analysis within 24-48 hours."
                }
              ].map((step, index) => (
                <div key={index} className="text-center">
                  <div className="inline-block bg-primary text-primary-foreground rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mb-4">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{step.title}</h3>
                  <p className="text-muted-foreground">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Drone Inspection Services Across Southern Ontario</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Proudly serving Toronto, Hamilton, Niagara Region, and the entire GTA
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {serviceAreas.map((area, index) => (
                <Card key={index} className="text-center">
                  <CardHeader>
                    <MapPin className="h-8 w-8 text-primary mx-auto mb-2" />
                    <CardTitle className="text-xl">{area.name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">{area.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="mt-12 text-center">
              <p className="text-lg text-muted-foreground mb-6">
                Don't see your area listed? We service all of Southern Ontario. Contact us to confirm availability.
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 bg-primary text-primary-foreground">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6">
              Ready to Schedule Your Drone Inspection?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Get a free quote today and discover why property owners throughout Ontario trust Blue Knight Contracting for professional aerial inspection services.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="secondary" asChild>
                <Link to="/contact">Request Free Quote</Link>
              </Button>
              <Button size="lg" variant="outline" className="bg-white/10 border-white text-white hover:bg-white/20" asChild>
                <a href="tel:4165552583">
                  <Phone className="mr-2 h-5 w-5" />
                  (416) 555-2583
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <FAQ 
              title="Frequently Asked Questions About Drone Inspections"
              description="Get answers to common questions about our aerial building inspection services"
              faqs={faqData}
            />
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="max-w-4xl mx-auto">
            <Card>
              <CardHeader className="text-center">
                <CardTitle className="text-2xl mb-2">
                  Serving Toronto, Hamilton, Niagara & the GTA
                </CardTitle>
                <CardDescription className="text-base">
                  Contact Blue Knight Contracting today for professional drone inspection services
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-6 text-center">
                  <div>
                    <Phone className="h-8 w-8 text-primary mx-auto mb-2" />
                    <p className="font-semibold">Call Us</p>
                    <a href="tel:4165552583" className="text-primary hover:underline">
                      (416) 555-2583
                    </a>
                  </div>
                  <div>
                    <Mail className="h-8 w-8 text-primary mx-auto mb-2" />
                    <p className="font-semibold">Email Us</p>
                    <a href="mailto:info@blueknightcontracting.ca" className="text-primary hover:underline">
                      info@blueknightcontracting.ca
                    </a>
                  </div>
                  <div>
                    <FileText className="h-8 w-8 text-primary mx-auto mb-2" />
                    <p className="font-semibold">Get a Quote</p>
                    <Link to="/contact" className="text-primary hover:underline">
                      Request Estimate
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    </>
  );
};

export default DroneInspectionServices;
